<script setup lang="ts">
import Badge from "primevue/badge";
import Calendar from "primevue/calendar";
import Checkbox from "primevue/checkbox";
import Listbox from "primevue/listbox";
import OverlayPanel from "primevue/overlaypanel";
import Tag from "primevue/tag";
import { computed, ref, watch } from "vue";
import { filterPillBarPresets } from "@/components/OperationsApp/components/FilterPillBar/styles.ts";
import {
  ORDER_STATUSES_OPTIONS,
  PRODUCT_TYPE_OPTIONS,
} from "@/components/OperationsApp/components/Filters/constants.ts";
import { filtersPresets } from "@/components/OperationsApp/components/Filters/styles.ts";
import { useFilters } from "@/components/OperationsApp/composables/useFilters.ts";
import { useProductTypeReasonFilters } from "@/components/OperationsApp/composables/useProductTypeReasonFilters.ts";
import { useSearchSessions } from "@/components/OperationsApp/composables/useSearchSessions.ts";
import { useUpdateDecisionDates } from "@/components/OperationsApp/composables/useUpdateDecisionDates.ts";
import { useGetCitiesData } from "@/providers/cities/useGetCitiesData.ts";
import type { Props } from "./types.ts";

defineOptions({
  name: "FilterPillBar",
});

const props = withDefaults(defineProps<Props>(), {});

const sessionPanelRef = ref();
const cityPanelRef = ref();
const libraryIdPanelRef = ref();
const reasonIdPanelRef = ref();
const decisionDatePanelRef = ref();
const orderStatusPanelRef = ref();

const { orderStatus, updateOrderStatus, city, updateCity, session, updateSession, clearFilters, status } =
  useFilters();

const { decisionDateStart, decisionDateEnd, dates, updateDecisionDates } = useUpdateDecisionDates();

const {
  libraryId,
  reasonId,
  isReasonEnabled,
  overrideReasonsData,
  updateReasonId,
  updateLibraryId,
  isProductTypeEnabled,
} = useProductTypeReasonFilters();

const {
  isLoadingOriginalSessionsOptions,
  isLoadingSearchSessionsOptions,
  sessionOptions,
  debouncedSessionNameUpdate,
  sessionLookupName,
} = useSearchSessions(status, session);

const { data: inUseCities, isLoading: isLoadingInUseCities } = useGetCitiesData({
  limit: 1000,
  page: 1,
  in_use: true,
});

const toggleSessionPanel = (event) => {
  sessionPanelRef.value?.toggle(event);
};
const toggleCityPanel = (event) => {
  cityPanelRef.value?.toggle(event);
};
const toggleLibraryIdPanel = (event) => {
  libraryIdPanelRef.value?.toggle(event);
};
const toggleReasonIdPanel = (event) => {
  reasonIdPanelRef.value?.toggle(event);
};
const toggleDecisionDatePanel = (event) => {
  decisionDatePanelRef.value?.toggle(event);
};
const toggleOrderStatusPanel = (event) => {
  orderStatusPanelRef.value?.toggle(event);
};

const showClearPill = computed(
  () =>
    session.value.length > 0 ||
    city.value.length > 0 ||
    libraryId.value.length > 0 ||
    reasonId.value.length > 0 ||
    decisionDateStart.value ||
    decisionDateEnd.value ||
    orderStatus.value.length > 0,
);

const clear = async () => {
  await clearFilters();
};

const updateCityCheckBox = (val) => {
  const foundIndex = city.value?.indexOf(val);
  if (foundIndex >= 0) {
    city.value.splice(foundIndex, 1);
  } else {
    city.value.push(val);
  }
  updateCity(city.value);
};
</script>

<template>
  <div class="ml-2 flex ">
    <Tag v-if="session.length > 0" value="Program Session" :pt="filterPillBarPresets.tag" class="ml-2" @click="toggleSessionPanel">
      <template #icon>
        <Badge class="ml-2 bg-black !text-sm h-[20px] min-w-[20px] !leading-5 !p-0 font-normal" :value="session.length" severity="contrast"></Badge>
      </template>
    </Tag>
    <OverlayPanel ref="sessionPanelRef">
      <div>
        <Listbox
          :model-value="session"
          @update:model-value="value => updateSession"
          :options="sessionOptions"
          filter
          multiple
          optionValue="id"
          optionLabel="name"
          class="w-full md:w-14rem !border-none mb-4"
          listStyle="max-height:250px"
          :pt="filtersPresets.listbox"
          filterPlaceholder="Search and Select"
          @filter="debouncedSessionNameUpdate"
          :selectionMessage="sessionLookupName"
        >
          <template #option="slotProps">
            <div class="flex align-items-center">
              <Checkbox v-model="session" @update:model-value="updateSession" :inputId="`${slotProps.option.id}`" name="session" :value="slotProps.option.id" :pt="filtersPresets.checkbox"/>
              <label :for="`${slotProps.option.id}`" class="ml-4 text-wrap">{{ slotProps.option.name }}</label>
            </div>
          </template>
        </Listbox>
      </div>
    </OverlayPanel>
    <Tag v-if="city.length > 0" value="City" :pt="filterPillBarPresets.tag" class="ml-2" @click="toggleCityPanel">
      <template #icon>
        <Badge class="ml-2 bg-black !text-sm h-[20px] min-w-[20px] !leading-5 !p-0 font-normal" :value="city.length" severity="contrast"></Badge>
      </template>
    </Tag>
    <OverlayPanel ref="cityPanelRef">
      <div v-if="!isLoadingInUseCities && inUseCities && inUseCities?.length > 0">
        <Listbox
          v-model="city"
          :options="inUseCities"
          filter
          multiple
          optionValue="id"
          optionLabel="city_ascii"
          class="w-full md:w-14rem !border-none mb-4"
          listStyle="max-height:250px"
          :pt="filtersPresets.listbox"
          filterPlaceholder="Search and Select"
        >
          <template #option="slotProps">
            <div class="flex align-items-center" @click="updateCityCheckBox(slotProps.option.id)">
              <Checkbox v-model="city" :inputId="`${slotProps.option.id}`" name="city" :value="slotProps.option.id" :pt="filtersPresets.checkbox"/>
              <label :for="`${slotProps.option.id}`" class="ml-4">{{ slotProps.option.city_ascii }}</label>
            </div>
          </template>
        </Listbox>
      </div>
    </OverlayPanel>
    <Tag v-if="libraryId.length > 0" value="Product Type" :pt="filterPillBarPresets.tag" class="ml-2" @click="toggleLibraryIdPanel">
      <template #icon>
        <Badge class="ml-2 bg-black !text-sm h-[20px] min-w-[20px] !leading-5 !p-0 font-normal" :value="libraryId.length" severity="contrast"></Badge>
      </template>
    </Tag>
    <OverlayPanel ref="libraryIdPanelRef">
      <div class="flex flex-col">
        <div v-for="productTypeOption of PRODUCT_TYPE_OPTIONS" :key="productTypeOption.key" class="flex items-center mb-4 ml-5">
          <Checkbox :disabled="!isProductTypeEnabled(productTypeOption.key)" v-model="libraryId" @update:model-value="updateLibraryId" :inputId="productTypeOption.key" name="productType" :value="productTypeOption.key" :pt="filtersPresets.checkbox"/>
          <label :for="productTypeOption.key" :class="['ml-4', {'!opacity-40 ': !isProductTypeEnabled(productTypeOption.key) }]">{{ productTypeOption.name }}</label>
        </div>
      </div>
    </OverlayPanel>
    <Tag v-if="reasonId.length > 0" value="Reason Type" :pt="filterPillBarPresets.tag" class="ml-2" @click="toggleReasonIdPanel">
      <template #icon>
        <Badge class="ml-2 bg-black !text-sm h-[20px] min-w-[20px] !leading-5 !p-0 font-normal" :value="reasonId.length" severity="contrast"></Badge>
      </template>
    </Tag>
    <OverlayPanel ref="reasonIdPanelRef">
      <div class="flex flex-col">
        <div v-for="overrideReasonsOption of overrideReasonsData?.overrideReasonsOptions" :key="overrideReasonsOption.id" class="flex items-center mb-4 ml-5">
          <Checkbox :disabled="!isReasonEnabled(overrideReasonsOption.id)" v-model="reasonId" @update:model-value="updateReasonId" :inputId="overrideReasonsOption.id" name="reasonType" :value="overrideReasonsOption.id" :pt="filtersPresets.checkbox"/>
          <label :for="overrideReasonsOption.id" :class="['ml-4', {'!opacity-40 ': !isReasonEnabled(overrideReasonsOption.id) }]">{{ overrideReasonsOption.name }}</label>
        </div>
      </div>
    </OverlayPanel>
    <Tag v-if="decisionDateStart || decisionDateEnd" value="Decision Date" :pt="filterPillBarPresets.tag" class="ml-2" @click="toggleDecisionDatePanel">
      <template #icon>
        <Badge class="ml-2 bg-black !text-sm h-[20px] min-w-[20px] !leading-5 !p-0 font-normal" :value="1" severity="contrast"></Badge>
      </template>
    </Tag>
    <OverlayPanel ref="decisionDatePanelRef">
      <Calendar
        class="mb-4 ml-2"
        :model-value="dates"
        selectionMode="range"
        :manualInput="false"
        showIcon
        :pt="filtersPresets.calendar"
        @update:model-value="updateDecisionDates"
      />
    </OverlayPanel>
    <Tag v-if="orderStatus.length > 0" value="Order Status" :pt="filterPillBarPresets.tag" class="ml-2" @click="toggleOrderStatusPanel">
      <template #icon>
        <Badge class="ml-2 bg-black !text-sm h-[20px] min-w-[20px] !leading-5 !p-0 font-normal" :value="orderStatus.length" severity="contrast"></Badge>
      </template>
    </Tag>
    <OverlayPanel ref="orderStatusPanelRef">
      <div class="flex flex-col">
        <div v-for="orderStatusOption of ORDER_STATUSES_OPTIONS" :key="orderStatusOption.key" class="flex items-center mb-4 ml-5">
          <Checkbox v-model="orderStatus" @update:model-value="updateOrderStatus" :inputId="orderStatusOption.key" name="orderStatus" :value="orderStatusOption.key" :pt="filtersPresets.checkbox"/>
          <label :for="orderStatusOption.key" class="ml-4">{{ orderStatusOption.name }}</label>
        </div>
      </div>
    </OverlayPanel>
    <Tag v-if="showClearPill" value="Clear" :pt="filterPillBarPresets.tag" class="ml-2" @click="clear">
      <template #icon>
        <span class="material-symbols-outlined">close_small</span>
      </template>
    </Tag>
  </div>
</template>

<style scoped>
</style>
